import { useMemo } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FormattedMessage, useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import FormikTextField from '../../../../shared/components/formik/FormikTextField'
import FormikSelectField from '../../../../shared/components/formik/FormikSelectField'
import FormikCheckboxField from '../../../../shared/components/formik/FormikCheckboxField'

const NetworkForm = ({ network, onSubmit, onCancel }) => {
  const intl = useIntl()
  const isEditing = Boolean(network)

  const driverOptions = useMemo(() => [
    {
      value: 'bridge',
      label: intl.formatMessage({ id: 'networks.drivers.bridge' })
    },
    {
      value: 'overlay',
      label: intl.formatMessage({ id: 'networks.drivers.overlay' })
    }
  ], [intl])

  const validationSchema = Yup.object({
    name: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    dockerName: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    driver: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    description: Yup.string(),
    external: Yup.boolean()
  })

  const formik = useFormik({
    initialValues: {
      name: network?.name || '',
      dockerName: network?.dockerName || '',
      driver: network?.driver || 'bridge',
      description: network?.description || '',
      external: network?.external || false
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      onSubmit(values)
        .then(() => {
          // Success handled by parent
        })
        .catch((err) => {
          setStatus(err.message || intl.formatMessage({ id: 'networks.form.error' }))
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <FormikTextField
        formik={formik}
        name="name"
        label={<FormattedMessage id="networks.form.nameLabel" />}
        autoFocus
      />

      <Box sx={{ mt: 2 }}>
        <FormikTextField
          formik={formik}
          name="dockerName"
          label={<FormattedMessage id="networks.form.dockerNameLabel" />}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikSelectField
          formik={formik}
          name="driver"
          label={<FormattedMessage id="networks.form.driverLabel" />}
          options={driverOptions}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikCheckboxField
          formik={formik}
          name="external"
          label={<FormattedMessage id="networks.form.externalLabel" />}
          helperText={intl.formatMessage({ id: 'networks.form.externalHelperText' })}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikTextField
          formik={formik}
          name="description"
          label={<FormattedMessage id="networks.form.descriptionLabel" />}
          multiline
          rows={3}
        />
      </Box>

      {formik.status && (
        <Typography color="error" sx={{ mt: 2 }}>
          {formik.status}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="app.actions.cancel.label" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={formik.isSubmitting}
        >
          <FormattedMessage
            id={isEditing ? 'networks.edit.submitButton' : 'networks.create.submitButton'}
          />
        </Button>
      </Box>
    </Box>
  )
}

export default NetworkForm
